import { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : { items: [], restaurant: null };
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const addItem = (item, restaurant) => {
    setCart((prev) => {
      // If cart has items from different restaurant, clear it
      if (prev.restaurant && prev.restaurant.id !== restaurant.id) {
        return {
          items: [{ ...item, quantity: 1 }],
          restaurant,
        };
      }

      const existingIndex = prev.items.findIndex((i) => i.id === item.id);
      if (existingIndex > -1) {
        const newItems = [...prev.items];
        newItems[existingIndex].quantity += 1;
        return { ...prev, items: newItems };
      }

      return {
        items: [...prev.items, { ...item, quantity: 1 }],
        restaurant: prev.restaurant || restaurant,
      };
    });
  };

  const removeItem = (itemId) => {
    setCart((prev) => {
      const newItems = prev.items.filter((i) => i.id !== itemId);
      return {
        items: newItems,
        restaurant: newItems.length > 0 ? prev.restaurant : null,
      };
    });
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity < 1) {
      removeItem(itemId);
      return;
    }

    setCart((prev) => ({
      ...prev,
      items: prev.items.map((i) =>
        i.id === itemId ? { ...i, quantity } : i
      ),
    }));
  };

  const clearCart = () => {
    setCart({ items: [], restaurant: null });
  };

  const getSubtotal = () => {
    return cart.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  const getItemCount = () => {
    return cart.items.reduce((sum, item) => sum + item.quantity, 0);
  };

  const getDeliveryFee = () => {
    return cart.restaurant?.delivery_fee || 0;
  };

  const getTotal = () => {
    return getSubtotal() + getDeliveryFee();
  };

  const value = {
    cart,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getSubtotal,
    getItemCount,
    getDeliveryFee,
    getTotal,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
